import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { FareBreakdown } from "@/components/FareBreakdown";

const FIELDS = [
  ["flat_fee", "First 3 miles flat fee ($)"],
  ["included_miles", "Included miles"],
  ["per_mile", "Per mile after included ($)"],
  ["per_minute", "Per minute ($)"],
  ["tax_rate", "Tax (%)"],
];
const r2 = (n) => Math.round(n * 100) / 100;
const toStrings = (d) => Object.fromEntries(FIELDS.map(([k]) => [k, String(d[k])]));

const preview = (p) => {
  const miles = 5, minutes = 10;
  const extra_miles = r2(Math.max(0, miles - p.included_miles));
  const extra_miles_charge = r2(extra_miles * p.per_mile);
  const time_charge = r2(minutes * p.per_minute);
  const subtotal = r2(p.flat_fee + extra_miles_charge + time_charge);
  const tax = r2(subtotal * p.tax_rate / 100);
  return { ...p, miles, minutes, extra_miles, extra_miles_charge, time_charge, subtotal, tax, total: r2(subtotal + tax) };
};

export const PricingSettings = () => {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    let alive = true;
    api.get("/admin/pricing").then(({ data }) => alive && setForm((f) => f || toStrings(data)));
    return () => { alive = false; };
  }, []);
  if (!form) return null;
  const num = Object.fromEntries(FIELDS.map(([k]) => [k, Number(form[k]) || 0]));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.put("/admin/pricing", num);
      setForm(toStrings(data));
      toast.success("Pricing saved — new estimates use these rates");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <form onSubmit={save} className="lux-card space-y-5 p-6" data-testid="pricing-form">
        <p className="lux-eyebrow">Fare formula</p>
        <p className="text-sm text-zinc-400">subtotal = flat fee (covers included miles) + per mile × miles beyond included + per minute × minutes; total = subtotal + tax %. Driver miles to pickup are not charged.</p>
        {FIELDS.map(([k, label]) => (
          <label key={k} className="block">
            <span className="lux-eyebrow mb-2 block">{label}</span>
            <input type="number" inputMode="decimal" min="0" step="0.01" required name={k} data-testid={`pricing-${k}-input`} className="lux-input"
              value={form[k]} onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, [k]: v })); }} />
          </label>
        ))}
        <button type="submit" disabled={busy} data-testid="pricing-save-button" className="btn-gold">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Save pricing</button>
      </form>
      <div className="lux-card p-6" data-testid="pricing-preview">
        <p className="lux-eyebrow mb-4">Preview · 5 mi, 10 min</p>
        <FareBreakdown fare={preview(num)} miles={5} minutes={10} />
      </div>
    </div>
  );
};
